import { useEffect, useRef, useState } from "react";
import { Row, Col } from "reactstrap";
import "./AiAssistant.css";
import {
  sendAiRewriteLessonRequest,
  sendAiRewritePPRequest,
  requestAiUserIntention,
  requestAiBasicQuestion,
  requestAiHelpLesson,
  requestAiHelpPracticeProblems,
  requestAiHelpDebugCode,
  requestAiHelpPP,
  requestAiContentHelp,
  requestAIGettingStarted,
  requestAlreadyLoggedInAiMessage,
  requestAlreadyLoggedOutAiMessage,
} from "../../services/RobotAIServices";
import { requestAnonSetCourse } from "../../services/userService";
import { useUserAuth } from "../../context/UserContext.js";
import { useNavigate, useLocation } from "react-router-dom";
import logoVideo from "../../assets/videos/CodenecAiCircle.mp4";
import DraggableComponent from "../Draggable/DraggableComponent.js";
import ChatMessageRenderer from "../ChatMessageRenderer/ChatMessageRenderer.js";
import VoiceRecorder from "../VoiceRecorder/VoiceRecorder.js";
import { useTranslation } from "react-i18next";
import { AiFirstUseNotice, AiMistakesNote } from "../AiNotice/AiNotice";

/**
 * Convert assistant response into a renderable chat message.
 * This lets your UI support either:
 * 1. normal text messages
 * 2. render_blocks messages
 */
function structureAssistantMessageResponse(res, friendlyFallbackTitle = "Response") {
  const fn = res;

  if (fn?.name === "render_blocks") {
    try {
      const args = typeof fn.arguments === "string" ? JSON.parse(fn.arguments) : fn.arguments;

      if (args && Array.isArray(args.blocks)) {
        return { role: "assistant", blocks: args.blocks };
      }

      return {
        role: "assistant",
        blocks: [
          { type: "title", text: friendlyFallbackTitle },
          { type: "text", markdown: "I returned an unexpected shape for render_blocks." },
          { type: "code", language: "json", code: JSON.stringify(args, null, 2) },
        ],
      };
    } catch (e) {
      return {
        role: "assistant",
        blocks: [
          { type: "title", text: friendlyFallbackTitle },
          { type: "callout", markdown: "I had trouble parsing my render_blocks arguments." },
          { type: "code", language: "json", code: String(fn.arguments ?? "") },
        ],
      };
    }
  }

  if (typeof res?.content === "string" && res.content.trim().length > 0) {
    return { role: "assistant", content: res.content };
  }

  if (typeof res?.arguments === "string" && res.arguments.trim().length > 0) {
    const args = JSON.parse(res.arguments);

    if (args.chatboxResponse) return { role: "assistant", content: args.chatboxResponse };
    if (args.questionToAsk) return { role: "assistant", content: args.questionToAsk };
  }

  return { role: "assistant", content: "_No content returned_" };
}

/**
 * Convert messages into plain content before sending to your AI service.
 * If a message was rendered with blocks, flatten those blocks into text/markdown.
 */
function structureMessageForRequest(messages) {
  return messages.map((msg) => {
    if (msg.role === "assistant" && msg.blocks) {
      const content = msg.blocks
        .map((block) => {
          switch (block.type) {
            case "title":
              return `## ${block.text}`;
            case "text":
              return block.text || block.markdown || "";
            case "code":
              return `\`\`\`${block.language || ""}\n${block.code}\n\`\`\``;
            case "callout":
              return `> ${block.text || block.markdown || ""}`;
            case "steps":
              return block.items.map((s, i) => `${i + 1}. ${s}`).join("\n");
            case "list":
              return block.items.map((item) => `- ${item}`).join("\n");
            default:
              return "";
          }
        })
        .join("\n\n");

      return { role: msg.role, content };
    }

    return msg.content !== undefined ? msg : { role: msg.role, content: "" };
  });
}

const AiAssistant = ({
  AiPageContex,
  setAiPageContext,
  embedded,
  currentLesson,
  onUpdateLesson,
  currentProblems,
  onUpdateProblems,
  currentCode,
  dockSelector = "#ai-robot-dock",
  openHelpRef,
  currentOuput,
  setCurrentOuput,
  pageContext
}) => {
  // =========================
  // LOCAL CHAT/UI STATE
  // =========================
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [isThinking, setIsThinking] = useState(false);


  // docked = robot icon is snapped into the navbar dock
  // dockHot = while dragging, whether it is near the dock target
  // dockEl = actual DOM element for the dock target
  const [docked, setDocked] = useState(true);
  const [dockHot, setDockHot] = useState(false);
  const [dockEl, setDockEl] = useState(null);

  // Parent can command DraggableComponent by writing to dragSetPosRef.current(...)
  const dragSetPosRef = useRef(null);

  // toggleRef = robot icon DOM
  // chatRef = chat box DOM
  const toggleRef = useRef(null);
  const chatRef = useRef(null);

  // Position for the chat box when open
  // This is NOT the draggable position of the robot icon.
  const [chatOffset, setChatOffset] = useState({ left: 90, top: -520 });

  const { signInAnonUser, user, setUser, logOut } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const DRAG_STORAGE_KEY = "chat-pos";

  // Easy-to-search log helper
  const debug = (...args) => console.log("[AiAssistant]", ...args);

  /**
   * Move the draggable robot icon so it visually snaps to the dock element.
   * This works by calculating the center of the dock target and placing
   * the robot icon on top of it.
   */
  const snapToDock = () => {
    if (!dockEl) {
      debug("snapToDock skipped: dockEl not found yet");
      return;
    }

    if (!toggleRef.current) {
      debug("snapToDock skipped: toggleRef not ready yet");
      return;
    }

    const dockRect = dockEl.getBoundingClientRect();
    const iconRect = toggleRef.current.getBoundingClientRect();

    const x = dockRect.left + dockRect.width / 2 - iconRect.width / 2;
    const y = dockRect.top + dockRect.height / 2 - iconRect.height / 2;

    debug("snapToDock calculated position", { x, y, pathname: location.pathname });

    if (dragSetPosRef.current) {
      dragSetPosRef.current({ x, y });
    } else {
      debug("snapToDock could not run: dragSetPosRef.current is null");
    }
  };

  /**
   * Find the navbar dock element in the DOM.
   * Example selector: #ai-robot-dock
   */
  useEffect(() => {
    if (typeof document === "undefined") return;

    const foundDock = document.querySelector(dockSelector);
    debug("finding dock element with selector:", dockSelector, "found:", foundDock);

    setDockEl(foundDock);
  }, [dockSelector, location.pathname]);

  /**
   * When route changes:
   * - reset assistant to docked mode
   * - close chat
   * - clear saved floating position
   *
   * This means every page starts from docked behavior again.
   */
  useEffect(() => {
    debug("route changed -> reset to docked mode", location.pathname);

    try {
      localStorage.removeItem(DRAG_STORAGE_KEY);
      debug("removed localStorage key:", DRAG_STORAGE_KEY);
    } catch (err) {
      debug("failed removing localStorage key:", err);
    }

    setDocked(true);
    setOpen(false);
  }, [location.pathname]);

  /**
   * Once dock element exists, try snapping.
   * We retry across several animation frames because the navbar/layout
   * may not be fully ready on the first frame after route change.
   */
  useEffect(() => {
    if (!dockEl) {
      debug("dockEl effect skipped because dockEl is null");
      return;
    }

    debug("dockEl found -> forcing docked closed state and trying to snap");
    setDocked(true);
    setOpen(false);

    let frames = 0;

    const trySnap = () => {
      frames += 1;
      debug("trySnap frame", frames);

      snapToDock();

      // Keep retrying for a short time.
      // Even if refs/layout are late, this helps catch up after navigation.
      if (frames < 20) {
        requestAnimationFrame(trySnap);
      }
    };

    requestAnimationFrame(trySnap);
  }, [dockEl]);

  /**
   * While docked:
   * - keep snapping on resize
   * - keep snapping on scroll
   * - keep snapping if dock element size changes
   *
   * This helps the robot stay attached to the navbar icon/dock.
   */
  useEffect(() => {
    if (!docked) {
      debug("docked-follow effect skipped because docked = false");
      return;
    }

    debug("starting dock-follow effect");

    let rafId = null;

    const scheduleSnap = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        debug("scheduleSnap -> snapToDock");
        snapToDock();
      });
    };

    // initial snap
    scheduleSnap();

    window.addEventListener("resize", scheduleSnap);
    window.addEventListener("scroll", scheduleSnap, { passive: true });

    let ro = null;
    if (dockEl && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => {
        debug("ResizeObserver noticed dock size/layout change");
        scheduleSnap();
      });
      ro.observe(dockEl);
    }

    // short follow loop after route/layout changes
    let frames = 0;
    const tick = () => {
      if (frames++ > 30) return;
      debug("dock-follow tick", frames);
      snapToDock();
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      debug("cleaning dock-follow effect");
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", scheduleSnap);
      window.removeEventListener("scroll", scheduleSnap);
      ro?.disconnect();
    };
  }, [docked, dockEl]);




  const ranEmbeddedInit = useRef(false);

  /**
   * Embedded version auto-sends initial AI message once.
   */
  useEffect(() => {
    if (!embedded) return;
    if (ranEmbeddedInit.current) return;

    debug("embedded mode initial sendMessage2");
    ranEmbeddedInit.current = true;
    sendMessage2();
  }, [embedded]);

  /**
   * Add/remove glow class on dock target when dragged near it.
   */
  useEffect(() => {
    if (!dockEl) return;

    debug("dockHot changed:", dockHot);
    dockEl.classList.toggle("dock-hot", dockHot);

    return () => dockEl.classList.remove("dock-hot");
  }, [dockHot, dockEl]);

  /**
   * If the dock element disappears, stop docking.
   */
  useEffect(() => {
    if (docked && !dockEl) {
      debug("dock element disappeared -> undocking");
      setDocked(false);
    }
  }, [docked, dockEl]);

  /**
   * Checks whether the dragged robot icon is close enough to the dock target
   * to snap back in.
   */
  const isNearDock = (x, y) => {
    if (!dockEl || !toggleRef.current) return false;

    const dockRect = dockEl.getBoundingClientRect();
    const r = toggleRef.current.getBoundingClientRect();

    const centerX = x + r.width / 2;
    const centerY = y + r.height / 2;

    const dx = centerX - (dockRect.left + dockRect.width / 2);
    const dy = centerY - (dockRect.top + dockRect.height / 2);

    const distance = Math.sqrt(dx * dx + dy * dy);
    const near = distance < 80;

    debug("isNearDock", { x, y, distance, near });
    return near;
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    const newMessages = [...messages, { role: "user", content: input }];
    setMessages(newMessages);
    setInput("");
    setIsThinking(true);

    try {
      const structured = structureMessageForRequest(newMessages);

      let responseData = await requestAiUserIntention(AiPageContex, structured);
      let res = responseData.function_call;

      let chatMessage = "";

      switch (res?.name) {
        case "rewriteLesson":
          try {
            const rewriteLessonResponse = await sendAiRewriteLessonRequest(
              structured,
              currentLesson,
              currentProblems,
              currentCode
            );

            const updatedLesson = JSON.parse(rewriteLessonResponse.function_call.arguments);
            const friendlyMessage =
              rewriteLessonResponse.content || "Here is your updated lesson.";

            if (updatedLesson) {
              onUpdateLesson(updatedLesson);
              chatMessage = friendlyMessage;
            } else {
              chatMessage = "Sorry, something went wrong with the lesson update.";
            }
          } catch (err) {
            console.error("Failed to parse lesson JSON:", err);
            chatMessage = "There was an error processing the rewritten lesson.";
          }
          break;

        case "rewritePracticeProblems":
          try {
            const rewritePPResponse = await sendAiRewritePPRequest(
              structured,
              currentLesson,
              currentProblems,
              currentCode
            );

            const updatedPP = JSON.parse(rewritePPResponse.function_call.arguments);
            const friendlyPPMessage =
              rewritePPResponse.content || "Here is your updated practice.";

            if (updatedPP) {
              onUpdateProblems(updatedPP);
              chatMessage = friendlyPPMessage;
            } else {
              chatMessage = "Sorry, something went wrong with the practice update.";
            }
          } catch (err) {
            console.error("Failed to parse practice JSON:", err);
            chatMessage = "There was an error processing the rewritten practice.";
          }
          break;



        // case "contentHelp": {
        //   const helpArguments = JSON.parse(res.arguments);
        //   if (helpArguments) {
        //     const helpMode = helpArguments.helpMode;
        //     responseData = await requestAiContentHelp(
        //       structured,
        //       currentLesson,
        //       currentProblems,
        //       currentCode,
        //       helpMode
        //     );
        //     res = responseData.function_call;
        //   }
        //   break;
        // }

        case "contentHelp": {
          const helpArguments = JSON.parse(res.arguments);
          if (helpArguments) {
            const helpMode = helpArguments.helpMode;
            responseData = await requestAiContentHelp(
              structured,
              currentLesson,
              currentProblems,
              currentCode,
              helpMode
            );
            res = responseData.function_call;
          }
          break;
        }

        case "helpLesson": {
          const responseData = await requestAiHelpLesson(structured, currentLesson, currentProblems, currentCode);
          res = responseData.function_call;
          break;
        }

        // case "helpPracticeProblems":
        //   responseData = await requestAiHelpPP(structured, currentLesson, currentProblems, currentCode);
        //   res = responseData.function_call;
        //   break;

        case "helpPracticeProblems": {
          const responseData = await requestAiHelpPracticeProblems(structured, currentLesson, currentProblems, currentCode);
          res = responseData.function_call;
          break;
        }

        case "debugCode": {
          const responseData = await requestAiHelpDebugCode(structured, currentLesson, currentProblems, currentCode, currentOuput);
          res = responseData.function_call;
          break;
        }


        case "navigatePage": {
          const normalized = structureAssistantMessageResponse(res, "Assistant");
          setMessages((prev) => [...prev, normalized]);

          const args = JSON.parse(res.arguments);

          if (args?.targetPage) {
            switch (args.targetPage) {
              case "login-page":
                if (user) {
                  const alreadyLoggedInResponse = await requestAlreadyLoggedInAiMessage();
                  const normalizedMessage = structureAssistantMessageResponse(
                    alreadyLoggedInResponse,
                    "Assistant"
                  );
                  setMessages((prev) => [...prev, normalizedMessage]);
                } else {
                  navigate(args.targetPage);
                }
                break;

              case "logout-page":
                if (!user) {
                  const alreadyLoggedOutResponse = await requestAlreadyLoggedOutAiMessage();
                  const normalizedMessage = structureAssistantMessageResponse(
                    alreadyLoggedOutResponse,
                    "Assistant"
                  );
                  setMessages((prev) => [...prev, normalizedMessage]);
                } else {
                  await logOut();
                  navigate("/");
                }
                break;

              default:
                navigate(args.targetPage);
            }
          }
          break;
        }

        case "clarifyIntent": {
          const normalizedMessage = structureAssistantMessageResponse(res, "Assistant");
          setMessages((prev) => [...prev, normalizedMessage]);
          break;
        }

        case "otherOrUnclear":
          responseData = await requestAiBasicQuestion(structured);
          res = responseData.function_call;
          break;

        default:
          break;
      }

      if (res?.name === "render_blocks") {
        const normalized = structureAssistantMessageResponse(res, "Assistant");
        setMessages((prev) => [...prev, normalized]);
      } else if (chatMessage) {
        setMessages((prev) => [...prev, { role: "assistant", content: chatMessage }]);
      }
    } catch (error) {
      console.error("AI request failed:", error);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong." },
      ]);
    } finally {
      setIsThinking(false);
    }
  };

  const sendMessage2 = async () => {
    if (!input.trim()) return;

    const newMessages = [...messages, { role: "user", content: input }];
    setMessages(newMessages);
    setInput("");
    setIsThinking(true);

    try {
      const res = await requestAIGettingStarted(newMessages);
      const chatMessage = res.content;

      setMessages([...newMessages, { role: "assistant", content: chatMessage }]);

      if (res.function_call?.name === "enrollStudentInCourse") {
        const { language } = JSON.parse(res.function_call.arguments);
        let anonUser = await signInAnonUser();
        anonUser = await requestAnonSetCourse(language, anonUser.uid, anonUser.userToken);
        await setUser(anonUser);

        if (language === "JavaScript") navigate("/JS_M1_L1");
      }
    } catch (error) {
      console.error("AI request failed:", error);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong." },
      ]);
    } finally {
      setIsThinking(false);
    }
  };


  //THIS METHOD IS USED TO SET THE AI IN A SPECIFIC LOCATION 
  const openHelpFromButton = () => {
    // Move the AI near the center of the screen
    const x = Math.max(20, window.innerWidth / 2 - 320);
    const y = Math.max(20, window.innerHeight / 2 - 180);

    if (dragSetPosRef.current) {
      dragSetPosRef.current({ x, y });
    }

    // Since we are moving it manually, it should no longer be docked
    setDocked(false);

    // Open the chat
    setOpen(true);

    // Add a starter message only if the chat is empty
    setMessages((prev) => {
      if (prev.length > 0) return prev;
      return [{ role: "assistant", content: "How can I help you?" }];
      let test = pageContext
    });
  };

  useEffect(() => {
    if (!openHelpRef) return;
    openHelpRef.current = openHelpFromButton;
    return () => {
      openHelpRef.current = null;
    };
  }, [openHelpRef, messages.length]);

  return (
    <div className={`${embedded ? "embedded" : ""}`}>

      {embedded ? (
        // EMBEDDED IN PAGE AI
        <Row>
          <Col className="col-10">
            {/* Outside the chat box: it has a fixed height, and the notice
                would squeeze its messages out of view. */}
            <div className="ai-embedded-notices">
              <AiFirstUseNotice voice />
            </div>
            <div className="chat-box embedded">
              <div className="chat-header">
                <span>{t("AiAssistant.needHelp")}</span>
              </div>

              <div className="chat-messages">
                {messages.map((msg, idx) => (
                  <div key={idx} className={msg.role}>
                    {msg.content}
                  </div>
                ))}

                {isThinking && (
                  <div className="assistant">
                    Thinking...
                  </div>
                )}
              </div>

              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("AiAssistant.typeYourMessage")}
              />

              <VoiceRecorder
                passSetVoiceText={setInput}
                lang={i18n.language === "es" ? "es-CR" : "en-US"}
              />

              <button onClick={sendMessage2}>{t("AiAssistant.send")}</button>
            </div>
            <div className="ai-embedded-notices">
              <AiMistakesNote />
            </div>
          </Col>

          <Col className="col-2">
            <video
              src={logoVideo}
              autoPlay
              loop
              muted
              playsInline
              className="landing-circle-video"
              draggable={false}
              style={{ width: 72, height: 72, display: "block" }}
            />
          </Col>
        </Row>
      ) : (
        <>
          {/* DRAGABLE AI */}
          {user && (
            <DraggableComponent
              className="border"
              storageKey={DRAG_STORAGE_KEY}
              docked={docked}
              setPositionRef={dragSetPosRef}
              handle=".chat-toggle"
              cancel=".chat-box, input, textarea, button"
              onDragStart={() => {
                debug("drag started, current docked =", docked);

                // As soon as dragging starts, stop docking mode.
                if (docked) {
                  debug("undocking because drag started");
                  setDocked(false);
                }
              }}
              onDrag={(p) => {
                debug("dragging", p);
                setDockHot(isNearDock(p.x, p.y));
              }}
              onDragEnd={(p) => {
                debug("drag ended", p);

                const near = isNearDock(p.x, p.y);
                setDockHot(false);

                if (near && dockEl) {
                  debug("drag ended near dock -> docking again");
                  setDocked(true);
                  setOpen(false);

                  requestAnimationFrame(() => snapToDock());
                } else {
                  debug("drag ended away from dock -> stay floating");
                }
              }}
            >
              <div className="ai-float-wrapper">
                <div
                  ref={toggleRef}
                  className="chat-toggle"
                  onClick={() => {
                    debug("robot clicked, toggling chat");
                    setOpen((prev) => !prev);
                  }}
                >
                  <video
                    src={logoVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="logo-video"
                    draggable={false}
                    style={{ width: 72, height: 72, display: "block" }}
                  />
                </div>

                {open && (
                  // STYLE POSITION CHAT RELATIVE TO THE AI IMAGE.
                  <div
                    ref={chatRef}
                    className="chat-box"
                    style={{
                      left: "100px",
                      top: "0px",
                    }}
                  >
                    <div className="chat-header">


                      <div className="chat-header-left">
                        <span>{t("AiAssistant.needHelp")}</span>
                      </div>

                      <button
                        className="chat-close-btn"
                        onClick={() => setOpen(false)}
                        type="button"
                      >
                        ×
                      </button>
                    </div>

                    <div className="chat-messages">
                      {messages.map((msg, idx) => (
                        <div key={idx} className={msg.role}>
                          {msg.blocks ? <ChatMessageRenderer blocks={msg.blocks} /> : msg.content}
                        </div>
                      ))}

                      {isThinking && (
                        <div className="assistant">
                          Thinking...
                        </div>
                      )}
                    </div>

                    <input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={t("AiAssistant.typeYourMessage")}
                    />

                    <VoiceRecorder
                      passSetVoiceText={setInput}
                      lang={i18n.language === "es" ? "es-CR" : "en-US"}
                    />

                    <button onClick={sendMessage}>{t("AiAssistant.send")}</button>
                  </div>
                )}
              </div>
            </DraggableComponent>
          )}
        </>
      )}
    </div>
  );
};

export default AiAssistant;